import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Search, X } from 'lucide-react'
import apiClient from '@/api/client'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { PromoScope } from '@/types'

interface PromoScopePickerProps {
  scope: PromoScope
  categoryIds: string[]
  productIds: string[]
  disabled?: boolean
  onChange: (next: { scope: PromoScope; categoryIds: string[]; productIds: string[] }) => void
}

/**
 * F-MKT-N2 — "Applies to" for a promo code: the whole bill, or only some
 * menu categories and/or individual items. The discount and the minimum
 * spend are then worked out on the matching items only (server-side).
 */
export function PromoScopePicker({ scope, categoryIds, productIds, disabled, onChange }: PromoScopePickerProps) {
  const [q, setQ] = useState('')
  const itemsMode = scope === 'items'

  const categoriesQuery = useQuery({
    queryKey: ['promo-scope', 'categories'],
    queryFn: () => apiClient.getCategories(false),
    enabled: itemsMode,
    staleTime: 5 * 60_000,
  })
  const productsQuery = useQuery({
    queryKey: ['promo-scope', 'products'],
    queryFn: () => apiClient.getAllProducts(),
    enabled: itemsMode,
    staleTime: 5 * 60_000,
  })

  const categories = useMemo(
    () => [...(categoriesQuery.data?.data ?? [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name)),
    [categoriesQuery.data]
  )
  const categoryName = useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories])
  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data])
  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products])

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return []
    return products.filter((p) => p.name.toLowerCase().includes(needle)).slice(0, 30)
  }, [products, q])

  const set = (patch: Partial<{ scope: PromoScope; categoryIds: string[]; productIds: string[] }>) =>
    onChange({ scope, categoryIds, productIds, ...patch })

  const toggleCategory = (id: string) =>
    set({ categoryIds: categoryIds.includes(id) ? categoryIds.filter((x) => x !== id) : [...categoryIds, id] })
  const toggleProduct = (id: string) =>
    set({ productIds: productIds.includes(id) ? productIds.filter((x) => x !== id) : [...productIds, id] })

  return (
    <div className="space-y-3 rounded-lg border border-border/70 p-3">
      <div className="space-y-1.5">
        <Label>Applies to</Label>
        <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5" role="radiogroup">
          {(
            [
              ['bill', 'Whole bill'],
              ['items', 'Some categories or items'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={scope === value}
              disabled={disabled}
              onClick={() => set({ scope: value })}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm transition-colors disabled:opacity-50',
                scope === value ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {itemsMode && (
        <>
          <p className="text-sm text-muted-foreground">
            The discount and minimum spend count only the matching items. Items that already have a manual discount are skipped.
          </p>

          <div className="space-y-1.5">
            <Label>Categories</Label>
            {categoriesQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading categories…</p>
            ) : categories.length === 0 ? (
              <p className="text-sm text-muted-foreground">No categories yet.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {categories.map((c) => {
                  const on = categoryIds.includes(c.id)
                  return (
                    <button
                      key={c.id}
                      type="button"
                      disabled={disabled}
                      aria-pressed={on}
                      onClick={() => toggleCategory(c.id)}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-sm transition-colors disabled:opacity-50',
                        on ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {on && <Check className="h-3.5 w-3.5 text-primary" />}
                      {c.name}
                      {!c.is_active && <span className="text-xs text-muted-foreground">(hidden)</span>}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="promo-scope-search">Individual items</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="promo-scope-search"
                className="pl-9"
                placeholder={productsQuery.isLoading ? 'Loading the menu…' : 'Search the menu to add an item'}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                disabled={disabled}
                autoComplete="off"
              />
            </div>
            {q.trim() !== '' && (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-border/70">
                {results.length === 0 ? (
                  <p className="p-2 text-sm text-muted-foreground">No items match.</p>
                ) : (
                  results.map((p) => {
                    const on = productIds.includes(p.id)
                    const viaCategory = !on && !!p.category_id && categoryIds.includes(p.category_id)
                    return (
                      <button
                        key={p.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => toggleProduct(p.id)}
                        className={cn('flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-muted/50', on && 'bg-primary/5')}
                      >
                        <span className="min-w-0 flex-1 truncate">{p.name}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {viaCategory ? `already in ${categoryName.get(p.category_id!)}` : p.category_id ? categoryName.get(p.category_id) : ''}
                        </span>
                        {on && <Check className="h-4 w-4 shrink-0 text-primary" />}
                      </button>
                    )
                  })
                )}
              </div>
            )}
            {productIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {productIds.map((id) => (
                  <span key={id} className="inline-flex items-center gap-1 rounded-lg border border-primary bg-primary/10 py-1 pl-2.5 pr-1 text-sm">
                    {productById.get(id)?.name ?? (productsQuery.isLoading ? '…' : 'Removed item')}
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleProduct(id)}
                      className="rounded p-0.5 text-muted-foreground hover:text-foreground"
                      aria-label="Remove item"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {categoryIds.length + productIds.length === 0 && (
            <p className="text-sm text-amber-700 dark:text-amber-400">Pick at least one category or item.</p>
          )}
        </>
      )}
    </div>
  )
}
